import { RouteLocationRaw } from "vue-router"
import { useAcl } from "vue-simple-acl"
import isEqual from "lodash/isEqual"

import { CustomError } from "@/models"
import { $keycloak, REAUTH_SESSION_KEY } from "@/plugins/keycloak"
import { wait } from "@/plugins/vuewait"
import { Actions, Getters, store } from "@/store"

export default async (
  to,
  from,
  next: (arg?: RouteLocationRaw) => void
): Promise<void | boolean> => {
  const auth = $keycloak.authenticated
  if (!auth) {
    next()
    return
  }

  /** Drop page selections as soon as their URL changes, including query-only Idea navigation. */
  if (to.params.epicId !== from.params.epicId) {
    await store.dispatch(Actions.RESET_EPIC)
  }
  if (to.query.idea !== from.query.idea) {
    await store.dispatch(Actions.RESET_IDEA)
  }
  if (!to.params.itemId && to.query.i !== from.query.i) {
    await store.dispatch(Actions.RESET_SESSION_ITEM_ID)
  }
  if (to.params.sprintId !== from.params.sprintId) {
    await store.dispatch(Actions.RESET_RETROSPECTIVE)
  }
  if (isEqual(to.params, from.params)) {
    next()
    return
  }
  const { params } = to

  /**
   * The page was interrupted by a logout or an expired session. It is restored only when the
   * account from the url is still available for the newly logged in user, otherwise the main page
   * of their own account is opened instead of the 404 page.
   */
  if (isReauthReturn() && params.accountId && !isAccountAvailable(params.accountId)) {
    return next("/")
  }

  try {
    if (params.accountId) {
      const storeAccount = store.getters[Getters.GET_SESSION_ACCOUNT]
      if (!useAcl().can("superAdmin")) {
        const profile = store.getters[Getters.GET_USER_CURRENT_PROFILE]
        const accountsDisabled = profile.accounts.every(a => a.enabled === false)
        const subscriptionDisabled = profile.accounts.every(a => a.subscriptionEnabled === false)
        if (accountsDisabled) {
          if (to.name === "DisabledAccount") {
            return next()
          }
          return next({
            name: "DisabledAccount",
            params: {
              accountId: to.params.accountId
            }
          })
        }
        if (subscriptionDisabled) {
          if (to.name === "TemporarilyDisabledAccount") {
            return next()
          }
          return next({
            name: "TemporarilyDisabledAccount",
            params: {
              accountId: to.params.accountId
            }
          })
        }
      }
      if (storeAccount?.urlName !== params.accountId) {
        wait.start("resolveAccount")
        const accountId = await store.dispatch(Actions.RESOLVE_SESSION_ACCOUNT, params.accountId)
        await Promise.all([
          store.dispatch(Actions.GET_SESSION_ACCOUNT, accountId),
          store.dispatch(Actions.STRIPE_GET_PRICES),
          store.dispatch(Actions.GET_ITEM_CONFIG),
          store.dispatch(Actions.HAS_AI_CONFIG, accountId),
          store.dispatch(Actions.GET_AI_PROFILE)
        ])
        wait.end("resolveAccount")
      }
    } else {
      await store.dispatch(Actions.RESET_SESSION_ACCOUNT)
    }

    if (!params.projectId) {
      await store.dispatch(Actions.RESET_SESSION_PROJECT)
    } else {
      await store.dispatch(Actions.RESOLVE_SESSION_PROJECT_ID, {
        name: params.projectId
      })
    }

    if (!params.teamId) {
      await store.dispatch(Actions.RESET_SESSION_TEAM)
    } else if (store.getters[Getters.GET_SESSION_TEAM]?.urlName !== params.teamId) {
      await store.dispatch(Actions.RESOLVE_SESSION_TEAM_ID, {
        name: params.teamId
      })
    }

    if (params.sprintId) {
      const teamId = store.getters[Getters.GET_SESSION_TEAM_ID]
      if (teamId) {
        await store.dispatch(Actions.RESOLVE_SESSION_SPRINT_ID, {
          sequenceNumber: params.sprintId,
          teamId
        })
      } else {
        console.error("Can not resolve sprint id because team id is null.")
      }
    } else {
      await store.dispatch(Actions.RESET_SESSION_SPRINT_ID)
    }

    if (params.itemId) {
      await store.dispatch(Actions.RESOLVE_ITEM_ID, {
        sequenceNumber: params.itemId
      })
    } else {
      await store.dispatch(Actions.RESET_SESSION_ITEM_ID)
    }

    if (params.epicId) {
      await store.dispatch(Actions.RESOLVE_EPIC_ID, params.epicId)
    }
  } catch (error) {
    console.error(error)
    return next({
      name: "Error",
      query: {
        code: error instanceof CustomError ? error.code : 404
      }
    })
  }
  return next()
}

/** The mark is read once, so a manually opened url later in the same tab still gets the 404 page. */
function isReauthReturn(): boolean {
  const isReauth = sessionStorage.getItem(REAUTH_SESSION_KEY)
  if (isReauth) {
    sessionStorage.removeItem(REAUTH_SESSION_KEY)
  }
  return Boolean(isReauth)
}

function isAccountAvailable(accountUrlName: string): boolean {
  if (useAcl().can("superAdmin")) {
    return true
  }

  const profile = store.getters[Getters.GET_USER_CURRENT_PROFILE]
  return Boolean(profile?.accounts?.some(account => account.urlName === accountUrlName))
}
